import './index.scss';

import { Component } from 'react';
import { BraxButton, BraxModals, BraxModalsType, BraxTourOnboarding } from 'design-system';

import t from '../../utils/i18n';
import { withContexts } from '../../utils/with-context';
import storage from '../../utils/storage';
import User from '../../view-model/users/User';
import UserContext from '../../contexts/User';

export type AppBarTourOnboardingProps = {
    modals: BraxModalsType,
    user: User,
    onEnd: () => void
};

export type AppBarTourOnboardingState = {
    running: boolean,
    complete: boolean
};

export default withContexts({
    modals: BraxModals,
    user: UserContext
}, class AppBarTourOnboarding extends Component<AppBarTourOnboardingProps, AppBarTourOnboardingState> {

    initialActive: Element | null = null;

    state = {
        running: false,
        complete: storage.getJSON<boolean>('brax.web-app.tour.complete') ?? false,
        skipped: storage.session.getJSON<boolean>('brax.web-app.tour.skipped') ?? false
    };

    close: (() => void) | null = null;

    componentDidMount() {
        if (!this.state.complete && !this.state.skipped)
            this.close = this.props.modals.create({
                className: 'app-bar-tour-onboarding-introduction',
                close: false,
                backdrop: false,
                children: (<>
                    <h3>{ t('Vamos conhecer o MeuPasseioVirtual!') }</h3>

                    <p>{ t('Este é um rápido tour que mostra as opções do menu e as principais telas da plataforma.') }</p>

                    <BraxButton variant="primary" onClick={() => this.setRunning(true)}>
                        { t('Começar Tour') }
                    </BraxButton>

                    <BraxButton variant="tertiary" onClick={() => this.skip()}>
                        { t('Fazer tour depois') }
                    </BraxButton>
                </>),
                onClose: () => this.setState({ running: true })
            });
        else
            this.props.onEnd();
    }

    componentWillUnmount() {
        this.close?.();
    }

    skip() {
        storage.session.set('brax.web-app.tour.skipped', true);

        this.setRunning(false);

        this.props.onEnd();
    }

    setRunning(running: boolean) {
        this.close?.();

        this.setState({ running });
    };

    render() {
        if (this.state.complete || !this.state.running)
            return null;

        return (
            <BraxTourOnboarding
                steps={[{
                    target: '.projects .brax-header .brax-button.new-project',
                    content: (<>
                        <h3>{ t('Criar novo projeto') }</h3>
    
                        <p>
                            { t('Clique neste botão para criar um novo Tour Virtual.') }
                            <br />
                            { t('Na tela que abrir, faça upload das suas imagens 360.') }
                        </p>
                    </>)
                }, {
                    target: '.brax-app-bar > nav > :nth-child(2) > :nth-child(1)',
                    content: (<>
                        <h3>{ t('Meus projetos') }</h3>
    
                        <p>{ t('Todos os projetos que você criar são apresentados nesta tela. Aqui você pode selecionar, editar, excluir e compartilhar os seus Tours Virtuais 360.') }</p>
                    </>),
                    placement: 'right'
                }, {
                    target: '.brax-app-bar > nav > :nth-child(2) > :nth-last-child(1)',
                    content: (<>
                        <h3>{ t('Configurações') }</h3>

                        <p>{ t('Aqui você cadastra a imagem da sua logomarca. Uma vez cadastrada, você pode utilizar esta logo na personalização dos seus Tours Virtuais 360.') }</p>
                    </>),
                    placement: 'right'
                }, {
                    target: '.brax-app-bar > nav > :nth-child(3) > :nth-child(1)',
                    content: (<>
                        <h3>{ t('Planos, funcionalidades e preços') }</h3>

                        <p>
                            { t('Conheça nossos planos de assinatura e confira os preços.') }
                            <br />
                            { t('Compare as funcionalidades de cada plano e verifique qual atende melhor as suas necessidades e demanda.') }
                        </p>
                    </>),
                    placement: 'right'
                }, {
                    target: '.brax-app-bar > nav > :nth-child(3) > :nth-child(2)',
                    content: (<>
                        <h3>{ t('Ajuda, cursos e atendimento') }</h3>

                        <p>
                            { t('Assista aos vídeos tutoriais que mostram como funcionam os principais recursos do MeuPasseioVirtual.') }
                            <br />
                            { t('Tenha acesso aos nossos cursos sobre Imagens 360, Tours Virtuais e Realidade Virtual.') }
                        </p>
                    </>),
                    placement: 'right'
                }, {
                    target: '.brax-app-bar > nav > :nth-child(3) > :nth-last-child(1)',
                    content: (<>
                        <h3>{ t('Meu perfil') }</h3>

                        <p>
                            { t('Aqui você configura as informações cadastrais de seu usuário, empresa e endereço.') }
                            <br />
                            { t('Visualize os dados do seu plano de assinatura contratado e confira o histórico de pagamentos.') }
                        </p>
                    </>),
                    placement: 'right'
                }]}
                onStateChange={(action, step) => {
                    const menu = document.querySelector<HTMLElement>('.brax-app-bar > nav');

                    if (menu)
                        menu.style.width = step.index === 0 || action === 'end' ? '' : '63px';

                    for (const menuGroup of Array.from(document.querySelectorAll<HTMLElement>('.brax-app-bar > nav > div')))
                        menuGroup.style.opacity = step.index === 0 || action === 'end' ? '' : '1';

                    if (action === 'start') {
                        const active = document.querySelector('.brax-app-bar > nav > div > .active')

                        if (active)
                            this.initialActive = active;
                    }

                    if (action === 'end') {
                        storage.set('brax.web-app.tour.complete', true);

                        this.initialActive?.classList.add('active');

                        this.setRunning(false);
                    } else {
                        document.querySelector('.brax-app-bar > nav > div > .active')?.classList.remove('active');

                        if (step.target !== '.projects .brax-header a')
                            document.querySelector(step.target)?.classList.add('active');
                    }
                }}
            />
        );
    }

});
